import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowLeft, FileText, CheckCircle, XCircle, Clock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminRequests = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [approveData, setApproveData] = useState({ amount_approved: "", repayment_plan_text: "" });
  const [declineReason, setDeclineReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      const response = await axios.get(`${API}/admin/help-requests`, {
        withCredentials: true
      });
      setRequests(response.data);
    } catch (error) {
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!approveData.amount_approved || !approveData.repayment_plan_text) {
      toast.error('Please fill all fields');
      return;
    }

    setActionLoading(true);
    try {
      await axios.post(
        `${API}/admin/help-request/${selectedRequest.request_id}/approve`,
        { 
          amount_approved: parseFloat(approveData.amount_approved),
          repayment_plan_text: approveData.repayment_plan_text
        },
        { withCredentials: true }
      );
      
      toast.success('Request approved!');
      setShowApproveDialog(false);
      setApproveData({ amount_approved: "", repayment_plan_text: "" });
      loadRequests();
    } catch (error) {
      toast.error('Failed to approve request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDecline = async () => {
    if (!declineReason) {
      toast.error('Please provide a reason');
      return;
    }

    setActionLoading(true);
    try {
      await axios.post(
        `${API}/admin/help-request/${selectedRequest.request_id}/decline`,
        { reason: declineReason },
        { withCredentials: true }
      );
      
      toast.success('Request declined');
      setShowDeclineDialog(false);
      setDeclineReason("");
      loadRequests();
    } catch (error) {
      toast.error('Failed to decline request');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const statusIcons = {
    pending: <Clock className="h-5 w-5 text-yellow-600" />,
    approved: <CheckCircle className="h-5 w-5 text-green-600" />,
    declined: <XCircle className="h-5 w-5 text-red-600" />
  };

  const statusColors = {
    pending: "bg-yellow-100 text-yellow-800",
    approved: "bg-green-100 text-green-800",
    declined: "bg-red-100 text-red-800"
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary/10 to-background py-12 px-6">
      <div className="container mx-auto max-w-7xl">
        <Button 
          data-testid="back-to-admin-btn"
          variant="ghost" 
          onClick={() => navigate('/admin')}
          className="mb-6 rounded-full"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Admin
        </Button>

        <div className="bg-card rounded-3xl shadow-soft p-8 md:p-12">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-heading font-bold mb-2" data-testid="requests-title">
                Help Requests
              </h1>
              <p className="text-muted-foreground">{requests.length} total requests</p>
            </div>
          </div>

          {requests.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No requests yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {requests.map((request) => (
                <div 
                  key={request.request_id}
                  data-testid={`request-${request.request_id}`}
                  className="p-6 border border-border rounded-2xl hover:shadow-soft transition-all"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-4 flex-1">
                      {statusIcons[request.status]}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-heading font-bold capitalize">
                            {request.bill_type.replace('-', ' ')}
                          </h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[request.status]}`}>
                            {request.status}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">
                          {request.user?.name} ({request.user?.email}) - {request.user?.city}, {request.user?.state}
                        </p>
                        <p className="text-sm mb-2">{request.description}</p>
                        <div className="flex gap-6 text-sm text-muted-foreground">
                          <span>Due: {new Date(request.due_date).toLocaleDateString()}</span>
                          <span>Submitted: {new Date(request.created_at).toLocaleDateString()}</span>
                          <span>Tier: <span className="capitalize">{request.user?.membership_tier || 'none'}</span></span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right ml-4">
                      <div className="text-3xl font-heading font-bold text-primary mb-2">
                        ${request.amount_requested}
                      </div>
                      {request.status === 'pending' && (
                        <div className="flex gap-2">
                          <Button
                            data-testid={`approve-${request.request_id}-btn`}
                            size="sm"
                            onClick={() => {
                              setSelectedRequest(request);
                              setApproveData({ 
                                amount_approved: request.amount_requested.toString(), 
                                repayment_plan_text: "" 
                              });
                              setShowApproveDialog(true);
                            }}
                            className="bg-green-600 hover:bg-green-700 text-white rounded-full"
                          >
                            Approve
                          </Button>
                          <Button
                            data-testid={`decline-${request.request_id}-btn`}
                            size="sm"
                            variant="destructive"
                            onClick={() => {
                              setSelectedRequest(request);
                              setShowDeclineDialog(true);
                            }}
                            className="rounded-full"
                          >
                            Decline
                          </Button>
                        </div>
                      )}
                      {request.status === 'declined' && request.decline_reason && (
                        <p className="text-sm text-red-600 mt-2">
                          Reason: {request.decline_reason}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Approve Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent data-testid="approve-dialog">
          <DialogHeader>
            <DialogTitle>Approve Request</DialogTitle>
            <DialogDescription>
              Enter the approved amount and repayment plan for this request.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="amount" data-testid="approve-amount-label">Amount to Approve</Label>
              <Input
                id="amount"
                data-testid="approve-amount-input"
                type="number"
                step="0.01"
                placeholder="150.00"
                value={approveData.amount_approved}
                onChange={(e) => setApproveData({...approveData, amount_approved: e.target.value})}
                className="input-default mt-2"
              />
            </div>
            <div>
              <Label htmlFor="repayment" data-testid="repayment-plan-label">Repayment Plan</Label>
              <Textarea
                id="repayment"
                data-testid="repayment-plan-textarea"
                placeholder="e.g., 5 weekly payments of $30"
                value={approveData.repayment_plan_text}
                onChange={(e) => setApproveData({...approveData, repayment_plan_text: e.target.value})}
                className="input-default mt-2"
              />
            </div>
            <div className="flex gap-3">
              <Button
                data-testid="confirm-approve-btn"
                onClick={handleApprove}
                disabled={actionLoading}
                className="btn-primary flex-1"
              >
                {actionLoading ? 'Approving...' : 'Confirm Approval'}
              </Button>
              <Button
                data-testid="cancel-approve-btn"
                onClick={() => setShowApproveDialog(false)}
                variant="outline"
                className="btn-outline flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Decline Dialog */}
      <Dialog open={showDeclineDialog} onOpenChange={setShowDeclineDialog}>
        <DialogContent data-testid="decline-dialog">
          <DialogHeader>
            <DialogTitle>Decline Request</DialogTitle>
            <DialogDescription>
              Please provide a reason for declining this request.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Textarea
              data-testid="decline-reason-textarea"
              placeholder="e.g., Amount exceeds tier limit"
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              className="input-default"
            />
            <div className="flex gap-3">
              <Button
                data-testid="confirm-decline-btn"
                onClick={handleDecline}
                disabled={actionLoading}
                variant="destructive"
                className="flex-1 rounded-full"
              >
                {actionLoading ? 'Declining...' : 'Confirm Decline'}
              </Button>
              <Button
                data-testid="cancel-decline-btn"
                onClick={() => setShowDeclineDialog(false)}
                variant="outline"
                className="btn-outline flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminRequests;
